export { wrapPageElement } from './src/templates/wrapPageElement';

const AMBASSADOR_UID = 'c911045a-68c1-4701-9058-9ff7f17de578';
const AMBASSADOR_SDK_BASE = 'https://snippet.sunlighten.com';
const AMBASSADOR_MAX_RETRIES = 2;
const POSTHOG_PERFORMANCE_SAMPLE_RATE = 0.2;
const SHOULD_CAPTURE_PERFORMANCE = Math.random() < POSTHOG_PERFORMANCE_SAMPLE_RATE;

const POSTHOG_ALLOWED_HOSTS = [
  'sunlighten.com',
  'www.sunlighten.com',
  'dev.beta.sunlighten.com',
  'staging.beta.sunlighten.com',
];

const POSTHOG_DEPLOYMENT_BY_HOST = {
  'sunlighten.com': { environment: 'production', branch: 'prod' },
  'www.sunlighten.com': { environment: 'production', branch: 'prod' },
  'dev.beta.sunlighten.com': { environment: 'development', branch: 'develop' },
  'staging.beta.sunlighten.com': { environment: 'stage', branch: 'stage' },
};

function capturePerformanceMetrics(posthog) {
  const capture = () => {
    try {
      const entries = performance.getEntriesByType('navigation');
      if (!entries || !entries.length) return;
      const nav = entries[0];

      const props = {
        ttfb: Math.round(nav.responseStart - nav.startTime),
        dom_interactive: Math.round(nav.domInteractive - nav.startTime),
        dom_complete: Math.round(nav.domComplete - nav.startTime),
        page_load: Math.round(nav.loadEventEnd - nav.startTime),
        dns_lookup: Math.round(nav.domainLookupEnd - nav.domainLookupStart),
        tcp_connect: Math.round(nav.connectEnd - nav.connectStart),
        tls_negotiate: Math.round(
          nav.secureConnectionStart > 0
            ? nav.connectEnd - nav.secureConnectionStart
            : 0
        ),
        transfer_size: nav.transferSize || 0,
        redirect_count: nav.redirectCount || 0,
      };

      if (typeof window.__longTaskCount === 'number') {
        props.long_task_count = window.__longTaskCount;
        props.long_task_total_ms = Math.round(window.__longTaskTotalDuration || 0);
      }

      posthog.capture('$performance_metrics', props);
    } catch (_) {
      // Performance API unavailable.
    }
  };

  if (document.readyState === 'complete') {
    setTimeout(capture, 100);
  } else {
    window.addEventListener('load', () => setTimeout(capture, 100), { once: true });
  }
}

function startLongTaskObserver() {
  if (typeof PerformanceObserver === 'undefined') return;
  window.__longTaskCount = 0;
  window.__longTaskTotalDuration = 0;
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        window.__longTaskCount++;
        window.__longTaskTotalDuration += entry.duration;
      }
    });
    observer.observe({ type: 'longtask', buffered: true });
  } catch (_) {
    // Long tasks are not supported in this browser.
  }
}

const loadPostHog = (isReconsent = false) => {
  if (!POSTHOG_ALLOWED_HOSTS.includes(window.location.hostname)) return;
  if (window.__posthogLoaded && window.posthog) {
    if (isReconsent && window.posthog.has_opted_out_capturing()) {
      window.posthog.opt_in_capturing();
      window.posthog.capture('$pageview');
    }
    return;
  }
  if (window.__posthogLoaded) return;
  if (!process.env.GATSBY_POSTHOG_API_KEY) return;
  window.__posthogLoaded = true;

  import('posthog-js').then(({ default: posthog }) => {
    posthog.init(process.env.GATSBY_POSTHOG_API_KEY, {
      api_host: process.env.GATSBY_POSTHOG_HOST || 'https://us.i.posthog.com',
      capture_pageview: false,
      capture_pageleave: true,
      capture_performance: {
        web_vitals: true,
        network_timing: true,
        web_vitals_allowed_metrics: ['LCP', 'CLS', 'FCP', 'INP'],
      },
      capture_exceptions: true,
      persistence: 'localStorage+cookie',
      autocapture: false,
      disable_session_recording: true,
      before_send: (event) => {
        if (
          ['$web_vitals', '$performance_metrics'].includes(event.event)
          && !SHOULD_CAPTURE_PERFORMANCE
        ) {
          return null;
        }
        return event;
      },
    });

    window.posthog = posthog;
    const deployment = POSTHOG_DEPLOYMENT_BY_HOST[window.location.hostname];

    posthog.register({
      hostname: window.location.hostname,
      environment: deployment.environment,
      branch: deployment.branch,
      site_region: process.env.GATSBY_SITE_REGION || 'us',
      deploy_sha: process.env.GATSBY_VERCEL_GIT_COMMIT_SHA || 'unknown',
      vercel_env: process.env.GATSBY_VERCEL_ENV || 'unknown',
    });

    window.__posthogInitialPageviewSent = true;
    posthog.capture('$pageview');
    capturePerformanceMetrics(posthog);
  }).catch(() => {
    window.__posthogLoaded = false;
  });
};

// Tear down all Ambassador DOM state and re-run the SDK bootstrap from
// scratch.  mbsy.ready() is a no-op after the SDK initialises (it just
// executes its callback), so the only way to recover from a failed
// iframe handshake — typically caused by Cloudflare bot-mitigation
// returning 403 + X-Frame-Options:SAMEORIGIN on raf.html — is to
// delete the global, re-create the queue shim, and re-insert the
// script tag so the SDK re-scans the DOM.
function rebootstrapAmbassador() {
  document.querySelectorAll('iframe[class*="mbsy"]').forEach(el => el.remove());
  document.querySelectorAll('[data-mbsy-integration]').forEach(el => {
    el.removeAttribute('data-mbsy-integration');
  });
  document.querySelectorAll('script[src*="snippet.sunlighten.com"]').forEach(el => el.remove());

  delete window.mbsy;
  delete window._mbsy;
  delete window.mbsyScriptExecuted;
  delete window.mbsyCb;
  delete window.mbsyEditor;
  delete window.__mbsyCustomDomains;
  delete window.__mbsyEtag;
  delete window.__mbsyData;

  (function (u, n, i, v, e, r, s, a, l) {
    u[r] = {};
    u[r].uid = AMBASSADOR_UID;
    u[r].m = ['getReferrerInfo', 'identify', 'integrationsReady', 'on', 'ready', 'track'];
    u[r].queue = [];
    u[r].f = function (t) {
      return function () {
        var l = Array.prototype.slice.call(arguments);
        l.unshift(t);
        u[r].queue.push(l);
        return u[r].queue;
      };
    };
    for (var t = 0; t < u[r].m.length; t++) {
      l = u[r].m[t];
      u[r][l] = u[r].f(l);
    }
    a = n.createElement(v);
    a.src = e + '/us-' + u[r].uid + '.js';
    a.async = s;
    n.getElementsByTagName(i)[0].appendChild(a);
  })(window, document, 'head', 'script', AMBASSADOR_SDK_BASE, 'mbsy', true);

  window.mbsy.ready(function () {});
}

// Schedule recovery checks: if the Ambassador iframe hasn't connected
// after the SDK's internal 10 s handshake timeout, tear everything down
// and re-bootstrap.  Two retries gives three total attempts.
function scheduleAmbassadorRecovery() {
  if (typeof window === 'undefined') return;
  if (!document.querySelector('script[src*="snippet.sunlighten.com"]') && !window.mbsy) return;

  window.__mbsyNavId = (window.__mbsyNavId || 0) + 1;
  const navId = window.__mbsyNavId;
  window.__mbsyRetryCount = 0;
  const check = () => {
    if (window.__mbsyNavId !== navId) return;
    if (window.__ambassadorConsentGranted === false) return;
    if (document.querySelector('iframe.mbsy-iframe-connected')) return;
    if (window.__mbsyRetryCount >= AMBASSADOR_MAX_RETRIES) return;
    window.__mbsyRetryCount++;
    rebootstrapAmbassador();
    setTimeout(check, 13000);
  };
  setTimeout(check, 12000);
}

export const onClientEntry = () => {
  if (typeof window !== 'undefined') {
    startLongTaskObserver();
    window.addEventListener('sunlighten:ambassador-started', scheduleAmbassadorRecovery);

    if (process.env.GATSBY_VERCEL_GIT_COMMIT_SHA) {
      console.info('ID:', process.env.GATSBY_VERCEL_GIT_COMMIT_SHA);
    }

    if (typeof window.gtag === 'function') {
      window.gtag('js', new Date());
      window.gtag('config', 'G-RFWWWR8X37', { send_page_view: false });
    }

    const loadMcfx = () => {
      if (window.__mcfxLoaded) return;
      window.__mcfxLoaded = true;

      window.mcfx = window.mcfx || function() {
        (window.mcfx.q = window.mcfx.q || []).push(arguments);
      };
      window.mcfx.e = 1 * new Date();
      window.mcfx.u = 'https://sl.sunlighten.com';
      const a = document.createElement('script');
      const m = document.getElementsByTagName('script')[0];
      a.async = 1;
      a.src = 'https://sl.sunlighten.com/mcfx.js';
      m.parentNode.insertBefore(a, m);
      window.mcfx('create', {
        siteId: 47581,
        modules: ['view', 'forms', 'reviews'],
        useSecureCookies: true,
      });

      const leadManager = document.createElement('script');
      leadManager.type = 'text/javascript';
      leadManager.async = true;
      leadManager.src = 'https://cdn.leadmanagerfx.com/phone/js/47581';
      document.head.appendChild(leadManager);
    };

    const loadOptiMonk = () => {
      if (window.__optimonkLoaded) return;
      window.__optimonkLoaded = true;

      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = 'https://onsite.optimonk.com/script.js?account=216404';
      document.head.appendChild(s);
    };

    const deferThirdParty = () => {
      const events = ['scroll', 'click', 'touchstart', 'keydown'];
      const handler = () => {
        events.forEach(e => window.removeEventListener(e, handler));
        setTimeout(() => {
          window.ketch('on', 'consent', function(consent) {
            if (consent.purposes.analytics) {
              loadMcfx();
              loadPostHog(true);
              window._wq = window._wq || [];
              window._wq.push(function(W) { W.consent(true); });
            } else {
              if (window.__posthogLoaded && window.posthog) {
                window.posthog.opt_out_capturing();
              }
              window._wq = window._wq || [];
              window._wq.push(function(W) { W.consent(false); });
            }
            if (consent.purposes.essential_services) loadOptiMonk();
          });
        }, 100);
      };
      events.forEach(e => window.addEventListener(e, handler, { once: true, passive: true }));
      setTimeout(handler, 5000);
    };
    deferThirdParty();

    // The Ambassador SDK requires a queued ready() call to trigger its
    // integration rendering pipeline.  Without this, the SDK initialises
    // internally (sets up _mbsy, fetches project config) but never
    // creates iframes or replaces the queue shim with real methods.
    if (typeof window.mbsy?.ready === 'function') {
      window.mbsy.ready(function () {});
    }
  }
}

// Clean up stale Ambassador iframes before Gatsby swaps the page tree.
// The SDK's built-in SPA watcher re-creates them after navigation via
// integrationCollection.init(), but leftover DOM from the previous page
// causes broken/invisible iframes. Pre-navigation teardown prevents this.
export const onPreRouteUpdate = () => {
  if (typeof window === 'undefined') return;
  document.querySelectorAll('iframe[class*="mbsy"]').forEach(el => el.remove());
  document.querySelectorAll('[data-mbsy-integration]').forEach(el => {
    el.removeAttribute('data-mbsy-integration');
  });
};

export const onRouteUpdate = ({ location }) => {
  if (typeof window === 'undefined') return;

  if (window.posthog) {
    if (window.__posthogInitialPageviewSent) {
      window.__posthogInitialPageviewSent = false;
    } else {
      window.posthog.capture('$pageview');
    }
  }

  if (location.hash) {
    setTimeout(() => {
      const anchorId = location.hash.substring(1);
      const targetElement = document.getElementById(anchorId);
      if (targetElement) {
        const offset = 100;
        const top =
          targetElement.getBoundingClientRect().top +
          window.scrollY -
          offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 100);
  }

  if (location.pathname === '/thank-you-pricing/' && !window.__ubTrackerLoaded) {
    window.ketch('on', 'consent', function(consent) {
      if (!consent.purposes.analytics || window.__ubTrackerLoaded) return;
      window.__ubTrackerLoaded = true;
      window._ubad = 'promo.sunlighten.com';
      window._ubaq = window._ubaq || [];
      window._ubaq.push(['trackGoal']);
      const ubScript = document.createElement('script');
      ubScript.type = 'text/javascript';
      ubScript.async = true;
      ubScript.src = 'https://promo.sunlighten.com/_ub/static/ets/t.js';
      document.head.appendChild(ubScript);
    });
  }

  scheduleAmbassadorRecovery();
}
